import type { CheckpointState } from '@ofmos/shared/checklist';
import { RECORD_QUERY_MAX_LIMIT } from '@ofmos/shared/record-query';
import { useMemo } from 'react';
import type { FieldMetadata, RecordRow } from '../../data/client';
import { useListedRecords, useRecordList } from '../../data/hooks';
import {
  type AgencyFacts,
  activeClients,
  type EnrollmentFacts,
  type HealthColour,
  isPaused,
  isWaiting,
} from '../fulfillment/dashboard/model';
import { useOnboardingBoard } from '../fulfillment/onboarding/useOnboardingBoard';
import { stepActorOf, useNamedFields } from '../fulfillment/shared';
import { todayKey } from '../record/dueDate';

/**
 * THE THREE SIDEBAR COUNTS (redesign register R-S2, quoted: "Clients = clients waiting for our
 * reply; Onboarding = late steps that wait on us; My tasks = overdue and due today"). Each is a
 * number of things somebody on the team has to do; nothing is shown at zero (R53).
 */

/**
 * Clients waiting for our reply, by the Dashboard's own Waiting rule (coordinator, 4 Oct), so the
 * number is the length of the Dashboard's "Waiting for our reply" list under All: the Dashboard's
 * active clients (`activeClients`: per agency its current enrollment, the agency not churned and
 * that enrollment active), red, not on pause (register D6, A13), whose agency passes
 * FE-FOUNDATION's waiting rule (`isWaiting`, register D2: marked handled after the message is not
 * waiting).
 */
export function waitingClientCount(
  enrollments: readonly EnrollmentFacts[],
  agencies: ReadonlyMap<string, AgencyFacts>,
  hasStatusField: boolean,
  today: string,
): number {
  return activeClients(enrollments, agencies, hasStatusField).filter(
    ({ enrollment, agency }) =>
      enrollment.health === 'Red' &&
      agency !== undefined &&
      !isPaused(agency, today) &&
      isWaiting(agency),
  ).length;
}

/**
 * Late steps that wait on us: every late step of every client on the Onboarding board whose
 * actor is the team (FE-FOUNDATION's actor map, register D4 and A9). `steps[i]` is the field
 * whose state is `states[i]` in each row, the board's own pairing.
 */
export function lateStepsOnUs(
  steps: readonly Pick<FieldMetadata, 'name'>[],
  rows: readonly { readonly states: readonly CheckpointState[] }[],
): number {
  let count = 0;
  for (const row of rows) {
    row.states.forEach((state, index) => {
      const step = steps[index];
      if (step !== undefined && state.status === 'late' && stepActorOf(step.name) === 'us') {
        count += 1;
      }
    });
  }
  return count;
}

function text(row: RecordRow, field: FieldMetadata | undefined): string | null {
  if (field === undefined) return null;
  const value = row.values[field.id];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function optionLabel(row: RecordRow, field: FieldMetadata | undefined): string | null {
  const id = text(row, field);
  if (id === null || field === undefined) return null;
  return field.options?.selectOptions?.find((option) => option.id === id)?.label ?? null;
}

function selectOf(fields: readonly (FieldMetadata | undefined)[]): string[] {
  return fields.flatMap((field) => (field === undefined ? [] : [field.id]));
}

const NO_IDS: readonly string[] = [];

/** The Clients count, or null while it loads or where the installation has no agencies. */
function useWaitingClients(): number | null {
  const agency = useNamedFields('agency');
  const enrollment = useNamedFields('cohort_enrollment');
  const unanswered = agency.field('unanswered_since');
  const handled = agency.field('reply_handled_at');
  const paused = agency.field('paused_until');
  const lifecycle = agency.field('lifecycle_status');
  const churnedAt = agency.field('churned_at');
  const enrollmentAgency = enrollment.field('agency');
  const status = enrollment.field('enrollment_status');
  const health = enrollment.field('health');
  const contractStart = enrollment.field('contract_start_date');

  const agencyIds = useRecordList(agency.object?.id ?? '', {
    select: selectOf([unanswered, handled, paused, lifecycle, churnedAt]),
    limit: RECORD_QUERY_MAX_LIMIT,
    enabled: agency.object !== undefined && unanswered !== undefined,
  });
  const enrollmentIds = useRecordList(enrollment.object?.id ?? '', {
    select: selectOf([enrollmentAgency, status, health, contractStart]),
    limit: RECORD_QUERY_MAX_LIMIT,
    enabled: enrollment.object !== undefined && enrollmentAgency !== undefined,
  });
  /* One cache subscription per list, not one observer per record (BR-PERF): the sidebar is on
     every page, so per-record observers here refetched every agency and enrollment on each
     change notice of those objects. */
  const agencies = useListedRecords(
    agency.object?.id ?? '',
    agencyIds.data?.ids ?? NO_IDS,
    agencyIds,
  );
  const enrollments = useListedRecords(
    enrollment.object?.id ?? '',
    enrollmentIds.data?.ids ?? NO_IDS,
    enrollmentIds,
  );

  return useMemo(() => {
    if (unanswered === undefined || agencyIds.data === undefined || agencies.isPending) {
      return null;
    }
    if (enrollmentIds.data === undefined || enrollments.isPending) return null;
    /* The Dashboard's facts (`useDashboardData.ts`), only the fields its Waiting rule reads. */
    const facts = new Map<string, AgencyFacts>();
    for (const row of agencies.records) {
      if (row.deletedAt !== null) continue;
      facts.set(row.id, {
        id: row.id,
        name: '',
        csmId: null,
        csmName: null,
        lastClientMessageAt: null,
        unansweredSince: text(row, unanswered),
        unansweredExcerpt: null,
        replyHandledAt: text(row, handled),
        pausedUntil: text(row, paused),
        lifecycle: optionLabel(row, lifecycle),
        churnedAt: text(row, churnedAt),
      });
    }
    const rows = enrollments.records
      .filter((row) => row.deletedAt === null)
      .map((row): EnrollmentFacts => {
        const colour = optionLabel(row, health);
        return {
          id: row.id,
          agencyId: text(row, enrollmentAgency),
          agencyLabel: null,
          status: optionLabel(row, status),
          phase: null,
          tier: null,
          health:
            colour === 'Red' || colour === 'Yellow' || colour === 'Green'
              ? (colour as HealthColour)
              : null,
          healthReason: null,
          healthSince: null,
          contractStartDate: text(row, contractStart),
          steps: [],
        };
      });
    return waitingClientCount(rows, facts, status !== undefined, todayKey());
  }, [
    unanswered,
    handled,
    paused,
    lifecycle,
    churnedAt,
    enrollmentAgency,
    status,
    health,
    contractStart,
    agencyIds.data,
    agencies.isPending,
    agencies.records,
    enrollmentIds.data,
    enrollments.isPending,
    enrollments.records,
  ]);
}

/** The Onboarding count, from the board the Onboarding page draws. */
function useLateStepsOnUs(): number | null {
  const board = useOnboardingBoard();
  return useMemo(
    () => (board.status === 'ready' ? lateStepsOnUs(board.steps, board.rows) : null),
    [board.status, board.steps, board.rows],
  );
}

export interface NavCounts {
  readonly clients: number | null;
  readonly onboarding: number | null;
}

/** The counts of Ben's two screens; My tasks counts in its own row (`TaskBadge`). */
export function useNavCounts(): NavCounts {
  return { clients: useWaitingClients(), onboarding: useLateStepsOnUs() };
}
